/**
 * rp-price-range.js
 * Progressive enhancement for Dawn's price_range facet inside .rp-coll collection pages.
 * Adds a dual-thumb <input type="range"> slider on top of the existing number inputs
 * (filter.v.price.gte / filter.v.price.lte) and keeps both in sync. Dispatches native
 * `input` + `change` events on the number inputs so Dawn's existing `facet-filters-form`
 * listener (bound with an `input` listener, debounced) still drives the AJAX filtering —
 * no dependency on any other script.
 *
 * Safe no-op when there is no `.facets__price` on the page (e.g. price filter disabled
 * or not present in Search & Discovery config yet).
 */
(function () {
  'use strict';

  function toNumber(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function fireEvents(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Match the slider's granularity to the number inputs' own `step` (if Dawn ever sets
  // one), else infer cents-vs-integer from whether the rendered `max`/`min` carries a
  // decimal point (Dawn's number-input values are always dot-decimal regardless of the
  // shop's display locale — see facets.liquid's uses_comma_decimals conversion).
  function getStep(minInput, maxInput) {
    var explicit = minInput.getAttribute('step') || maxInput.getAttribute('step');
    if (explicit) return explicit;
    var sample = (maxInput.getAttribute('max') || minInput.getAttribute('max') || minInput.value || maxInput.value || '');
    return /[.,]/.test(sample) ? '0.01' : '1';
  }

  function enhance(priceRangeEl) {
    var inputs = priceRangeEl.querySelectorAll('input[type="number"]');
    if (inputs.length < 2) return;

    var minInput = inputs[0];
    var maxInput = inputs[1];

    if (priceRangeEl.querySelector('.rp-price-slider')) return; // already enhanced

    var rangeMax = toNumber(
      priceRangeEl.getAttribute('data-max') || maxInput.getAttribute('max') || minInput.getAttribute('max'),
      100
    );
    var rangeMin = 0;

    var minValue = toNumber(minInput.value, rangeMin);
    var maxValue = toNumber(maxInput.value, rangeMax);
    var step = getStep(minInput, maxInput);

    var wrapper = document.createElement('div');
    wrapper.className = 'rp-price-slider';

    var track = document.createElement('div');
    track.className = 'rp-price-slider__track';

    var range = document.createElement('div');
    range.className = 'rp-price-slider__range';

    var minRange = document.createElement('input');
    minRange.type = 'range';
    minRange.className = 'rp-price-slider__input rp-price-slider__input--min';
    minRange.min = rangeMin;
    minRange.max = rangeMax;
    minRange.step = step;
    minRange.value = minValue;
    minRange.setAttribute('aria-label', 'Minimum price');

    var maxRange = document.createElement('input');
    maxRange.type = 'range';
    maxRange.className = 'rp-price-slider__input rp-price-slider__input--max';
    maxRange.min = rangeMin;
    maxRange.max = rangeMax;
    maxRange.step = step;
    maxRange.value = maxValue;
    maxRange.setAttribute('aria-label', 'Maximum price');

    track.appendChild(range);
    wrapper.appendChild(track);
    wrapper.appendChild(minRange);
    wrapper.appendChild(maxRange);

    priceRangeEl.appendChild(wrapper);

    function updateTrack() {
      var lo = toNumber(minRange.value, rangeMin);
      var hi = toNumber(maxRange.value, rangeMax);
      var span = rangeMax - rangeMin || 1;
      var left = ((lo - rangeMin) / span) * 100;
      var right = ((hi - rangeMin) / span) * 100;
      range.style.left = left + '%';
      range.style.right = 100 - right + '%';
    }

    function onMinRangeInput(e) {
      // Dawn's facet-filters-form listens for `input` at the form level (delegated) and debounces
      // an AJAX refetch off of it; without this the drag itself -- not just the eventual release --
      // would spam that listener on every tick. The number inputs still get their own `input`
      // event dispatched deliberately, only on release (onMinRangeChange/onMaxRangeChange below).
      if (e) e.stopPropagation();
      var lo = toNumber(minRange.value, rangeMin);
      var hi = toNumber(maxRange.value, rangeMax);
      if (lo > hi) {
        lo = hi;
        minRange.value = lo;
      }
      updateTrack();
    }

    function onMaxRangeInput(e) {
      if (e) e.stopPropagation();
      var lo = toNumber(minRange.value, rangeMin);
      var hi = toNumber(maxRange.value, rangeMax);
      if (hi < lo) {
        hi = lo;
        maxRange.value = hi;
      }
      updateTrack();
    }

    function onMinRangeChange() {
      // At the slider's own floor (0), leave the number input blank rather than writing an
      // explicit gte=0 into the filter URL -- 0 is the same as "no minimum" for this shop.
      var lo = toNumber(minRange.value, rangeMin);
      minInput.value = lo <= rangeMin ? '' : minRange.value;
      fireEvents(minInput);
    }

    function onMaxRangeChange() {
      // Symmetric with onMinRangeChange: at the slider's own ceiling, blank rather than lte=max.
      var hi = toNumber(maxRange.value, rangeMax);
      maxInput.value = hi >= rangeMax ? '' : maxRange.value;
      fireEvents(maxInput);
    }

    minRange.addEventListener('input', onMinRangeInput);
    maxRange.addEventListener('input', onMaxRangeInput);
    minRange.addEventListener('change', onMinRangeChange);
    maxRange.addEventListener('change', onMaxRangeChange);

    // Keep the slider in sync if the number inputs change from elsewhere (typed value,
    // or Dawn's own PriceRange custom element clamping on `change`). Thumbs can't cross:
    // re-clamp the other thumb the same way the two range inputs clamp each other above.
    minInput.addEventListener('change', function () {
      var lo = toNumber(minInput.value, rangeMin);
      var hi = toNumber(maxRange.value, rangeMax);
      if (lo > hi) lo = hi;
      minRange.value = lo;
      updateTrack();
    });
    maxInput.addEventListener('change', function () {
      var hi = toNumber(maxInput.value, rangeMax);
      var lo = toNumber(minRange.value, rangeMin);
      if (hi < lo) hi = lo;
      maxRange.value = hi;
      updateTrack();
    });

    updateTrack();
  }

  function init() {
    var scopes = document.querySelectorAll('.rp-coll .facets__price');
    if (!scopes.length) return;
    scopes.forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after Dawn's AJAX facet re-render swaps #main-collection-filters innerHTML.
  var filtersRoot = document.getElementById('main-collection-filters');
  if (filtersRoot && window.MutationObserver) {
    new MutationObserver(function () {
      init();
    }).observe(filtersRoot, { childList: true, subtree: true });
  }
})();
